import {
  AdjacentShopperDetails,
  GroupedAssignedItems,
} from '../../../batchDetails/types';
import {Box, HBox} from '../../../components/core/view';
import {CustomText, H5Text} from '../../../components/core/text';
import React, {useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';

import If from '../../../components/conditional/If';
import {Modules} from '@buncha/utils/types';
import ProfileIcon from '../../../components/core/icon/ProfileIcon';
import {ShopperScreens} from '@buncha/home/navigation/types';
import UserNameUpperPill from '@buncha/common/components/VipProfileIcon';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';

interface CustomerOrderInfoProps {
  groupedAssignedItems?: GroupedAssignedItems;
  hideFooter?: boolean;
  hasNewUser?: boolean;
  isVip?: boolean;
  buyerName?: string;
  isPackingScreen?: boolean;
  itemsPacked?: number;
  hasBunchaClub?: boolean;
  adjShopperDetails?: AdjacentShopperDetails;
}

function CustomerOrderInfo(props: CustomerOrderInfoProps) {
  const {groupedAssignedItems, hideFooter, adjShopperDetails} = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {body} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const showUpperPill =
    props?.hasNewUser || props?.isVip || props?.hasBunchaClub;
  const customerTypeString = useMemo(() => {
    if (props?.hasBunchaClub) return body?.customerType?.hasBunchaClub;
    if (props?.isVip) return body?.customerType?.isVIP;
    if (props?.hasNewUser) return body?.customerType?.newCustomer;
    return '';
  }, [
    body?.customerType?.hasBunchaClub,
    body?.customerType?.isVIP,
    body?.customerType?.newCustomer,
    props?.hasBunchaClub,
    props?.hasNewUser,
    props?.isVip,
  ]);

  const adjShopperText = adjShopperDetails
    ? `${body?.coShopper}${adjShopperDetails?.assignee?.firstName} ${
        adjShopperDetails?.assignee?.lastName?.[0] ?? ''
      }.`
    : '';

  return (
    <HBox style={styles.parent}>
      <ProfileIcon viewBox="-3 -1 25 25" fill={theme?.text.blue[300]} />
      <Box style={[styles.textContainer]}>
        <If condition={showUpperPill}>
          <UserNameUpperPill
            color={
              !props?.isVip && props?.hasNewUser
                ? theme?.text.green[100]
                : undefined
            }
            text={customerTypeString}
          />
        </If>
        <H5Text
          style={[
            styles.nameText,
            {
              fontFamily: muliFont[700].normal,
              color: theme?.text.blue[300],
            },
          ]}>
          {groupedAssignedItems?.groupedKey
            ? groupedAssignedItems?.groupedKey
            : props?.buyerName}
        </H5Text>
        <If condition={!hideFooter && !props?.isPackingScreen}>
          <CustomText
            style={[
              styles.nameText,
              {
                fontFamily: muliFont[600].normal,
                color: theme?.text.gray[100],
              },
            ]}>
            {groupedAssignedItems?.orderItems?.length}
            {body?.itemsPacked}
            {adjShopperText}
          </CustomText>
        </If>
        <If condition={!hideFooter && props?.isPackingScreen}>
          <CustomText
            style={[
              styles.nameText,
              {
                fontFamily: muliFont[600].normal,
                color: theme?.text.gray[100],
              },
            ]}>
            {props?.itemsPacked
              ? `${props.itemsPacked}${body?.itemsPacked}${adjShopperText}`
              : `${groupedAssignedItems?.orderItems?.length}${body?.itemsPicked}${adjShopperText}`}
          </CustomText>
        </If>
      </Box>
    </HBox>
  );
}

export default React.memo(CustomerOrderInfo);
